import { useMemo, useState, useEffect } from "react";
import { FileUpload } from "../../../type";
import { useLoaderHelper } from "../../../../../helper/loaderHelper";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../components/auth";
import { DataType } from "ka-table/enums";
import { ReviewAndUploadForm } from "./ReviewAndUploadForm";
import { handleSubmit } from "./handleSubmit";
import { Formik } from "formik";
import { JobValidationSchema } from "./validationSchema";
import { updatePagination } from "../utility";
import { message } from "../../../../../components/toast";

export const sanitizeKey = (key: string): string => {
  return key?.replace(/[^a-zA-Z0-9]/g, '_');
};

const ReviewAndUpload = (props: any) => {
  const [selectedRowIdsBySheet, setSelectedRowIdsBySheet] = useState<{ [key: number]: number[] }>({});
  const [selectedRowsDataBySheet, setSelectedRowsDataBySheet] = useState<{ [key: number]: any[] }>({});
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedSheetIndex, setSelectedSheetIndex] = useState<number>(0);
  const [isInitialMount, setIsInitialMount] = useState<boolean>(true);
  const [isSelectionValid, setIsSelectionValid] = useState<boolean>(true);
  const { fileUpload, app, api, closeAddUpdate, isUpdate, selectedFunction, columnMappingData, onSelectionChange } = props;
  const loader = useLoaderHelper();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const currentMappings = columnMappingData?.find((item: any) => item.fileIndex === selectedSheetIndex)?.mappings || [];

  const reviewColumns = currentMappings
    .filter((item: any) => item.sourceColumn)
    .map((item: any) => ({
      key: sanitizeKey(item.sourceColumn),
      title: item.sourceColumn,
      dataType: DataType.String,
      destinationColumn: item.destinationColumn,
      destinationLName: item.destinationColumnLabelName,
    })) || [];

  const matchedSheet = fileUpload[selectedSheetIndex]?.selectedSheet?.[0];
  const sheetName = matchedSheet?.name;
  const matchedTotalSheet = fileUpload[selectedSheetIndex]?.totalSheet?.find((sheet: any) => sheet.name === sheetName);
  const rowData = matchedTotalSheet?.rowData || [];
  const sourceColumns = matchedTotalSheet?.sourceColumnList?.[0] || [];

  // const mappedRows = useMemo(() => {
  //   const startIndex = currentPage * pageSize;
  //   const endIndex = startIndex + pageSize;
  //   const columnIndexMap = sourceColumns?.reduce((map:any, column:any, index:any) => {
  //     map[column?.trim()] = index;
  //     return map;
  //   }, {} as { [key: string]: number });

  //   return rowData?.map((row: any[], index: number) => {
  //     const rowObj: any = { tableId: index + 1, srNo: index + 1 };
  //     columnMappingData?.forEach((mapping: any) => {
  //       if (mapping?.sourceColumn) {
  //         const sourceIndex = columnIndexMap[mapping?.sourceColumn?.trim()];
  //         const sanitizedKey = sanitizeKey(mapping.sourceColumn);
  //         rowObj[sanitizedKey] = sourceIndex !== undefined ? row[sourceIndex] ?? "" : "";
  //       }
  //     });
  //     return rowObj;
  //   }).slice(startIndex, endIndex);
  // }, [rowData, columnMappingData, currentPage, pageSize, sourceColumns]);

  const mappedRows = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    const columnIndexMap = sourceColumns?.reduce((map: any, column: any, index: any) => {
      map[column?.trim()] = index;
      return map;
    }, {} as { [key: string]: number });

    return rowData?.map((row: any[], index: number) => {
      const rowObj: any = { tableId: index + 1, srNo: index + 1 };
      currentMappings.forEach((mapping: any, colIndex: number) => {
        if (mapping.sourceColumn) {
          const sourceIndex = columnIndexMap[mapping?.sourceColumn?.trim()];
          const sanitizedKey = sanitizeKey(mapping.sourceColumn);
          rowObj[sanitizedKey] = sourceIndex !== undefined ? row[sourceIndex] ?? "" : "";
          // const sourceIndex = sourceColumns.indexOf(mapping.sourceColumn);
          // rowObj[mapping.sourceColumn] = sourceIndex !== -1 ? row[sourceIndex] ?? "" : "";
        }
      });
      return rowObj;
    }).slice(startIndex, endIndex);
  }, [rowData, currentMappings, currentPage, pageSize, sourceColumns]);

  const totalRows = rowData.length;

  useEffect(() => {
    if (isInitialMount && fileUpload && fileUpload.length > 0) {
      const newSelectedRowIdsBySheet: { [key: number]: number[] } = {};
      const newSelectedRowsDataBySheet: { [key: number]: any[] } = {};

      fileUpload.forEach((file: FileUpload, index: number) => {
        const sheet = file.selectedSheet?.[0];
        const totalSheet = file.totalSheet?.find((s: any) => s.name === sheet?.name);
        const sheetRowData = totalSheet?.rowData || [];
        const sheetSourceColumns = totalSheet?.sourceColumnList?.[0] || [];
        const sheetMappings = columnMappingData?.find((item: any) => item.fileIndex === index)?.mappings || [];

        if (sheetRowData.length > 0) {
          const allRowIds = sheetRowData.map((_: any, rowIndex: number) => rowIndex + 1);
          newSelectedRowIdsBySheet[index] = allRowIds;

          const allRowsData = sheetRowData.map((row: any[], rowIndex: number) => {
            const rowObj: any = { tableId: rowIndex + 1 };
            sheetMappings.forEach((mapping: any) => {
              if (mapping.sourceColumn) {
                const sourceIndex = sheetSourceColumns.indexOf(mapping.sourceColumn);
                rowObj[mapping.sourceColumn] = sourceIndex !== -1 ? row[sourceIndex] ?? "" : "";
              }
            });
            return rowObj;
          });
          newSelectedRowsDataBySheet[index] = allRowsData;
        } else {
          newSelectedRowIdsBySheet[index] = [];
          newSelectedRowsDataBySheet[index] = [];
        }
      });
      setSelectedRowIdsBySheet(newSelectedRowIdsBySheet);
      setSelectedRowsDataBySheet(newSelectedRowsDataBySheet);
      if (onSelectionChange) onSelectionChange(newSelectedRowsDataBySheet[selectedSheetIndex] || []);
      setIsInitialMount(false);
    }
  }, [isInitialMount, fileUpload, columnMappingData, onSelectionChange, selectedSheetIndex]);

  // Update isSelectionValid whenever selectedRowIdsBySheet or fileUpload changes
  useEffect(() => {
    const invalidSheets: string[] = [];
    fileUpload.forEach((file: FileUpload, index: number) => {
      const sheet = file.selectedSheet?.[0];
      const totalSheet = file.totalSheet?.find((s: any) => s.name === sheet?.name);
      const sheetRowData = totalSheet?.rowData || [];
      const selectedRowIds = selectedRowIdsBySheet[index] || [];

      if (sheetRowData.length > 0 && selectedRowIds.length === 0) {
        invalidSheets.push(`${file.file.name} - ${sheet?.name || "No Sheet"}`);
      }
    });
    setIsSelectionValid(invalidSheets.length === 0);
  }, [fileUpload, selectedRowIdsBySheet]);

  const handleSelectionChange = (actionType: string, selectedRowKeys: any) => {
    let newSelectedIds: number[] = [];
    let selectedData: any[] = [];

    if (actionType === "SelectAllRows") {
      newSelectedIds = rowData.map((_: any, index: number) => index + 1);
      selectedData = rowData.map((row: any[], index: number) => {
        const rowObj: any = { tableId: index + 1 };
        currentMappings.forEach((mapping: any) => {
          if (mapping.sourceColumn) {
            const sourceIndex = sourceColumns.indexOf(mapping.sourceColumn);
            rowObj[mapping.sourceColumn] = sourceIndex !== -1 ? row[sourceIndex] ?? "" : "";
          }
        });
        return rowObj;
      });
    } else if (actionType === "DeselectAllRows") {
      newSelectedIds = [];
      selectedData = [];
      message("warning", "All rows deselected. Please select at least one row to proceed.");
    } else if (Array.isArray(selectedRowKeys)) {
      newSelectedIds = selectedRowKeys;
      selectedData = rowData
        .map((row: any[], index: number) => {
          const rowObj: any = { tableId: index + 1 };
          currentMappings.forEach((mapping: any) => {
            if (mapping.sourceColumn) {
              const sourceIndex = sourceColumns.indexOf(mapping.sourceColumn);
              rowObj[mapping.sourceColumn] = sourceIndex !== -1 ? row[sourceIndex] ?? "" : "";
            }
          });
          return rowObj;
        })
        .filter((row: any) => newSelectedIds.includes(row.tableId));
    } else {
      if (actionType === "SelectRow") {
        newSelectedIds = [...(selectedRowIdsBySheet[selectedSheetIndex] || []), selectedRowKeys];
      } else if (actionType === "DeselectRow") {
        newSelectedIds = (selectedRowIdsBySheet[selectedSheetIndex] || []).filter(
          (id) => id !== selectedRowKeys
        );
      }
      selectedData = rowData
        .map((row: any[], index: number) => {
          const rowObj: any = { tableId: index + 1 };
          currentMappings.forEach((mapping: any) => {
            if (mapping.sourceColumn) {
              const sourceIndex = sourceColumns.indexOf(mapping.sourceColumn);
              rowObj[mapping.sourceColumn] = sourceIndex !== -1 ? row[sourceIndex] ?? "" : "";
            }
          });
          return rowObj;
        })
        .filter((row: any) => newSelectedIds.includes(row.tableId));
    }

    setSelectedRowIdsBySheet((prev) => ({
      ...prev,
      [selectedSheetIndex]: newSelectedIds,
    }));
    setSelectedRowsDataBySheet((prev) => ({
      ...prev,
      [selectedSheetIndex]: selectedData,
    }));
    if (onSelectionChange) onSelectionChange(selectedData);
  };

  return (
    <Formik
      initialValues={{
        name: "",
        startDate: undefined,
        endDate: undefined,
        jobStartWithSubmit: false,
      }}
      validationSchema={app.currentApp.appCode === "rdo" ? JobValidationSchema : undefined}
      onSubmit={(values) => {
        const invalidDateSheets: string[] = [];
        fileUpload.forEach((file: FileUpload, index: number) => {
          const sheet = file.selectedSheet?.[0];
          const totalSheet = file.totalSheet?.find((s: any) => s.name === sheet?.name);
          if ((totalSheet as any)?.detectedDateInfo?.isMatching === false) {
            invalidDateSheets.push(`${file.file.name} - ${sheet?.name || "No Sheet"}`);
          }
        });

        if (invalidDateSheets.length > 0) {
          console.log("invalidDateSheets", invalidDateSheets);

          message("warning", `The company date and excel sheet date formats are not matching in sheets: ${invalidDateSheets.join(", ")}. Kindly update in the excel sheet to submit.`);
          return;
        }
        const invalidSheets: string[] = [];
        fileUpload.forEach((file: FileUpload, index: number) => {
          const sheet = file.selectedSheet?.[0];
          const totalSheet = file.totalSheet?.find((s: any) => s.name === sheet?.name);
          const sheetRowData = totalSheet?.rowData || [];
          const selectedRowIds = selectedRowIdsBySheet[index] || [];

          if (sheetRowData.length > 0 && selectedRowIds.length === 0) {
            invalidSheets.push(`${file.file.name} - ${sheet?.name || "No Sheet"}`);
          }
        });

        if (invalidSheets.length > 0) {
          message("warning", `Please select at least one record in sheets: ${invalidSheets.join(", ")}.`);
          return;
        }

        handleSubmit(
          values,
          fileUpload,
          selectedRowIdsBySheet,
          selectedRowsDataBySheet,
          selectedSheetIndex,
          app,
          api,
          closeAddUpdate,
          isUpdate,
          selectedFunction,
          loader,
          navigate,
          location,
          auth
        );
      }}
    >
      {(formProps) => (
        <ReviewAndUploadForm
          formProps={formProps}
          fileUpload={fileUpload}
          selectedSheetIndex={selectedSheetIndex}
          setSelectedSheetIndex={setSelectedSheetIndex}
          setCurrentPage={setCurrentPage}
          reviewColumns={reviewColumns}
          mappedRows={mappedRows}
          rowData={rowData}
          selectedRowIdsBySheet={selectedRowIdsBySheet}
          handleSelectionChange={handleSelectionChange}
          currentPage={currentPage}
          rowCount={totalRows}
          limit={pageSize}
          updatePagination={() => updatePagination(setCurrentPage, setPageSize)}
          app={app}
          matchedSheet={matchedSheet}
          isSelectionValid={isSelectionValid}
        />
      )}
    </Formik>
  );
};

export default ReviewAndUpload;